import React, { createContext, useContext } from "react";
import { TOPICS } from "@/pages/knowledge/topics";

export const ActiveTopicContext = createContext(null);

export function Topic({ id, title, children }) {
  const activeId = useContext(ActiveTopicContext);
  if (activeId && activeId !== id) return null;

  const index = TOPICS.findIndex((t) => t.id === id);
  const num = index >= 0 ? String(index + 1).padStart(2, "0") : "—";
  const group = index >= 0 ? TOPICS[index].group : "";

  return (
    <article
      id={id}
      data-testid={`kh-topic-${id}`}
      className="scroll-mt-24"
    >
      <div className="font-mono text-xs font-bold text-[#084392] tracking-[0.22em]">
        {num} / {group.toUpperCase()}
      </div>
      <h2 className="font-heading mt-3 text-3xl md:text-4xl lg:text-5xl tracking-tighter leading-[1.05]">
        {title}
      </h2>
      <div className="mt-6 prose-tc">{children}</div>
    </article>
  );
}

export function P({ children }) {
  return <p className="text-[#334155] leading-relaxed mb-4">{children}</p>;
}

export function H3({ children }) {
  return (
    <h3 className="font-heading text-2xl md:text-3xl mt-8 mb-3 tracking-tight">
      {children}
    </h3>
  );
}

export function H4({ children }) {
  return (
    <h4 className="font-heading text-xl mt-6 mb-2 tracking-tight text-[#0f172a]">
      {children}
    </h4>
  );
}

export function UL({ children }) {
  return (
    <ul className="list-disc pl-5 space-y-1.5 text-[#334155] mb-4">{children}</ul>
  );
}

export function Callout({ children, kind = "info" }) {
  const styles = {
    info: "bg-[#e8eef8] border-[#084392]/20 text-[#0f172a]",
    warn: "bg-[#fef3c7]/50 border-[#f59e0b]/30 text-[#0f172a]",
    danger: "bg-[#fee2e2]/50 border-[#ef4444]/30 text-[#0f172a]",
    success: "bg-[#d1fae5]/50 border-[#10b981]/30 text-[#0f172a]",
  };
  return (
    <div className={`rounded-xl border p-4 mb-4 ${styles[kind]}`}>{children}</div>
  );
}

export function Table({ headers, rows }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-[#e2e8f0] mb-4">
      <table className="w-full text-sm">
        <thead className="bg-[#f8fafc]">
          <tr>
            {headers.map((h, i) => (
              <th
                key={i}
                className="text-left font-mono text-[10px] font-bold text-[#475569] tracking-[0.15em] py-2.5 px-3"
              >
                {h.toUpperCase()}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-[#e2e8f0]">
              {r.map((c, j) => (
                <td key={j} className="py-2.5 px-3 text-[#334155] align-top">
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function VisualBox({ title, children }) {
  return (
    <div className="rounded-xl border border-[#e2e8f0] bg-white p-5 mb-4">
      {title && (
        <div className="font-mono text-[10px] font-bold text-[#475569] tracking-[0.18em] mb-3">
          {title}
        </div>
      )}
      {children}
    </div>
  );
}

export function Quote({ children }) {
  return (
    <blockquote className="border-l-4 border-[#084392] pl-4 italic text-[#475569] my-4">
      {children}
    </blockquote>
  );
}
